// ── Plantillas de WhatsApp ───────────────────────────────────────────────────
// Para escribir FUERA de la ventana de 24 horas. Meta las aprueba y cada envío
// se cobra — distinto de las respuestas guardadas (useSavedReplies), que son
// texto propio para pegar dentro de la ventana, sin aprobación y sin costo.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

/** `borrador` es nuestro; el resto lo dice Meta. */
export type EstadoDePlantilla =
  | 'borrador' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED' | 'DISABLED';

export interface WhatsappTemplate {
  id: number;
  title: string;
  /** El nombre técnico en Meta. Null mientras es borrador. */
  name: string | null;
  language: string;
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  body: string;
  variables: string[];
  status: EstadoDePlantilla;
  rejectionReason: string | null;
  syncedAt: string | null;
}

/** Lo que la bandeja necesita para mandar una. */
export interface PlantillaEnviable {
  id: number;
  title: string;
  body: string;
  variables: string[];
}

const CLAVE = ['whatsapp-templates'] as const;

export function useWhatsappTemplates() {
  return useQuery<{ items: WhatsappTemplate[] }>({
    queryKey: CLAVE,
    queryFn: () => api.get('/api/whatsapp-templates'),
  });
}

/** Las aprobadas, que son las únicas que se pueden enviar. */
export function usePlantillasEnviables(enabled = true) {
  return useQuery<{ items: PlantillaEnviable[] }>({
    queryKey: [...CLAVE, 'aprobadas'],
    queryFn: () => api.get('/api/whatsapp-templates/aprobadas'),
    enabled,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; body: string; category?: string; variables?: string[] }) =>
      api.post<WhatsappTemplate>('/api/whatsapp-templates', input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CLAVE }); },
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number; title?: string; body?: string; category?: string; variables?: string[] }) =>
      api.patch<WhatsappTemplate>(`/api/whatsapp-templates/${id}`, patch),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CLAVE }); },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/whatsapp-templates/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CLAVE }); },
  });
}

/** Mandarla a aprobación. Irreversible: el texto deja de poder editarse. */
export function useSubmitTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<WhatsappTemplate>(`/api/whatsapp-templates/${id}/submit`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CLAVE }); },
  });
}

/** Traer de Meta el estado de cada una. */
export function useSyncTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ revisadas: number; cambiadas: number }>('/api/whatsapp-templates/sync', {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CLAVE }); },
  });
}

/** Mandar una plantilla a una conversación. Es un mensaje real, y se cobra. */
export function useEnviarPlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { threadId: number; templateId: number; valores: string[] }) =>
      api.post<{ ok: boolean; platformMessageId?: string }>('/api/whatsapp-templates/send', input),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['dm-thread', vars.threadId] });
      void qc.invalidateQueries({ queryKey: ['dm-inbox'] });
    },
  });
}
